import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 6;
const EDGE = 8;
const MAX_HEIGHT = 320;

/**
 * The shell's own select: a trigger and a listbox in a portal (so a scrolling
 * panel cannot clip it). The highlight springs between options the way the
 * sidebar's does, the chosen option carries a tick, and the list opens from
 * the trigger, flipping above it when there is no room below.
 *
 * options: [{ value, label }]; values are compared with JSON, so objects work.
 * variant: 'boxed' (an .edl-input, for Controls) or 'bare' (underlined, for
 * toolbars).
 */
export function Dropdown({ id, value, options, onChange, placeholder = 'Choose…', variant = 'boxed', className = '', 'aria-label': ariaLabel }) {
  const autoId = useId();
  const listId = `${id ?? autoId}-list`;
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [active, setActive] = useState(-1);
  const [place, setPlace] = useState(null);
  const typed = useRef({ text: '', at: 0 });
  const selected = options.findIndex((o) => same(o.value, value));

  const show = (at = selected) => {
    setClosing(false);
    setActive(at >= 0 ? at : 0);
    setOpen(true);
  };
  const hide = ({ focus = true } = {}) => {
    if (!open) return;
    setOpen(false);
    setClosing(true);
    setTimeout(() => setClosing(false), 240); // if animationend never comes
    if (focus) triggerRef.current?.focus();
  };
  const choose = (i) => {
    if (i >= 0 && !same(options[i].value, value)) onChange?.(options[i].value);
    hide();
  };

  // Place the list under the trigger (or above, if it fits better there).
  useLayoutEffect(() => {
    if (!open) return;
    const t = triggerRef.current.getBoundingClientRect();
    const view = triggerRef.current.ownerDocument.defaultView;
    const below = view.innerHeight - t.bottom - GAP - EDGE;
    const above = t.top - GAP - EDGE;
    const natural = Math.min(MAX_HEIGHT, listRef.current?.scrollHeight ?? MAX_HEIGHT);
    const up = below < natural && above > below;
    const width = Math.max(t.width, 180);
    setPlace({
      left: Math.max(EDGE, Math.min(t.left, view.innerWidth - width - EDGE)),
      width,
      maxHeight: Math.min(MAX_HEIGHT, up ? above : below),
      ...(up ? { bottom: view.innerHeight - t.top + GAP } : { top: t.bottom + GAP }),
      up,
    });
  }, [open]);

  // Close on outside press, outside scroll and resize.
  useEffect(() => {
    if (!open) return undefined;
    const doc = triggerRef.current.ownerDocument;
    const view = doc.defaultView;
    const outside = (e) => !listRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target);
    const onDown = (e) => outside(e) && hide({ focus: false });
    const onScroll = (e) => outside(e) && hide({ focus: false });
    const onResize = () => hide({ focus: false });
    doc.addEventListener('pointerdown', onDown, true);
    view.addEventListener('scroll', onScroll, true);
    view.addEventListener('resize', onResize);
    view.addEventListener('blur', onResize);
    return () => {
      doc.removeEventListener('pointerdown', onDown, true);
      view.removeEventListener('scroll', onScroll, true);
      view.removeEventListener('resize', onResize);
      view.removeEventListener('blur', onResize);
    };
  }, [open]);

  // Keep the active option in view while moving with the keyboard.
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelectorAll('[role="option"]')[active]?.scrollIntoView({ block: 'nearest' });
  }, [active, open, place]);

  const onKeyDown = (e) => {
    const last = options.length - 1;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        show(selected >= 0 ? selected : e.key === 'ArrowUp' ? last : 0);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        setActive((i) => Math.min(last, i + 1));
        break;
      case 'ArrowUp':
        setActive((i) => Math.max(0, i - 1));
        break;
      case 'Home':
      case 'PageUp':
        setActive(0);
        break;
      case 'End':
      case 'PageDown':
        setActive(last);
        break;
      case 'Enter':
      case ' ':
        if (e.key === ' ' && typed.current.text) return typeahead(e);
        choose(active);
        break;
      case 'Escape':
        hide();
        break;
      case 'Tab':
        hide({ focus: false });
        return;
      default:
        return typeahead(e);
    }
    e.preventDefault();
    e.stopPropagation();
  };

  // Typing jumps to the first option that starts with what was typed.
  const typeahead = (e) => {
    if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    const now = Date.now();
    typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLowerCase(), at: now };
    const hit = options.findIndex((o) => String(o.label).toLowerCase().startsWith(typed.current.text));
    if (hit >= 0) {
      if (open) setActive(hit);
      else onChange?.(options[hit].value);
    }
  };

  const label = selected >= 0 ? options[selected].label : placeholder;
  const doc = triggerRef.current?.ownerDocument ?? (typeof document !== 'undefined' ? document : null);

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`edl-dropdown edl-dropdown--${variant}${variant === 'boxed' ? ' edl-input' : ''}${open ? ' is-open' : ''} ${className}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? hide() : show())}
        onKeyDown={onKeyDown}
      >
        <span className={`edl-dropdown__value${selected < 0 ? ' is-placeholder' : ''}`}>{label}</span>
        <svg className="edl-dropdown__chevron" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </button>
      {(open || closing) &&
        doc &&
        createPortal(
          <List
            listRef={listRef}
            id={listId}
            options={options}
            selected={selected}
            active={active}
            place={place}
            closing={closing}
            onClosed={() => setClosing(false)}
            onHover={setActive}
            onChoose={choose}
            label={ariaLabel}
          />,
          doc.body,
        )}
    </>
  );
}

function List({ listRef, id, label, options, selected, active, place, closing, onClosed, onHover, onChoose }) {
  const [glow, setGlow] = useState(null);
  const ready = useRef(false);

  useLayoutEffect(() => {
    const item = listRef.current?.querySelectorAll('[role="option"]')[active];
    setGlow(item ? { top: item.offsetTop, height: item.offsetHeight } : null);
  }, [active, place]);
  useEffect(() => {
    const raf = requestAnimationFrame(() => (ready.current = true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={listRef}
      id={id}
      role="listbox"
      aria-label={label}
      className={`edl-dropdown-list${place?.up ? ' is-up' : ''}${closing ? ' is-closing' : ''}`}
      style={place ? { left: place.left, top: place.top, bottom: place.bottom, minWidth: place.width, maxHeight: place.maxHeight } : { visibility: 'hidden', left: 0, top: 0 }}
      onAnimationEnd={(e) => e.target === e.currentTarget && closing && onClosed()}
      onPointerLeave={() => onHover(selected)}
    >
      {glow && <span className={`edl-dropdown-list__glow${ready.current ? ' is-ready' : ''}`} style={{ transform: `translateY(${glow.top}px)`, height: glow.height }} aria-hidden="true" />}
      {options.map((o, i) => (
        <div
          key={i}
          id={`${id}-${i}`}
          role="option"
          aria-selected={i === selected}
          className={`edl-dropdown-list__option${i === active ? ' is-active' : ''}`}
          onPointerMove={() => i !== active && onHover(i)}
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => onChoose(i)}
        >
          <svg className="edl-dropdown-list__tick" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.6" />
          </svg>
          <span className="edl-dropdown-list__label">{o.label}</span>
        </div>
      ))}
    </div>
  );
}

const same = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);
